import { ChangeEvent, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../../components/Alert';

import ProgressBar from '../../components/ProgressBar';
import ProgressStep from '../../components/ProgressStep';
import RenderDocuments from '../../components/RenderDocuments';
import Checkbox from '../../components/Checkbox';
import Button from '../../components/Button';
import StepInterface from '../../interfaces/Step';
import StepClass from '../../entities/Step';
import DefaultContract from './DefaultContract';
import ServiceRegistration from '../../services/RegisterService';
import { gtmAttributesSignedContract } from '../../utils/gtmVariables';
import useGTMEventsTracker from '../../hooks/useGTMEventsTracker';
import { gtmAttributesUpdateContractPage } from '../../utils/gtmVariables';
import AppConfig from '../../config/AppConfig';
import { getStorage } from '../../helpers/Storage';
import Typography from '../../components/Typography';

const serviceRegistration = new ServiceRegistration();

interface Contract {
  sellerEntrance: string;
  type: string;
  url: string;
}

interface Seller {
  contract: Contract;
  contractHash: string;
}
interface SellerProps {
  seller: Seller;
}

const Contract = () => {
  const [show, setShow] = useState(false);
  const [error, setError] = useState(false);
  const [warning, setWarning] = useState(false);
  const [acceptContract, setAcceptContract] = useState(false);
  const [disableButton, setDisableButton] = useState(false);
  const [seller, setSeller] = useState<SellerProps>();
  const [defaultContractText, setDefaultContractText] = useState('Assinar e continuar');

  const Step = new StepClass();

  const seller_entrance = seller?.seller.contract.sellerEntrance;
  const contract_hash = seller?.seller.contractHash;
  const BASE_URL_CONTRACT_HUNTING = `${AppConfig.get(
    'API_BASE_URL_MP_BEM_VINDO'
  )}api/v2/site/seller/contract/`;
  const contract_url = `${BASE_URL_CONTRACT_HUNTING}${contract_hash}`;

  const navigate = useNavigate();
  const gtmEventsTracker = useGTMEventsTracker();

  useEffect(() => {
    gtmEventsTracker(gtmAttributesUpdateContractPage);
    setSeller(JSON.parse(getStorage('seller')));
  }, []);

  const handleAcceptContract = (e: ChangeEvent<HTMLInputElement>) => {
    setAcceptContract(e.target.checked);
  };

  const handleClick = async () => {
    setAcceptContract(true);
    setDisableButton(true);
    setDefaultContractText('Carregando...');
    gtmEventsTracker(gtmAttributesSignedContract);
    try {
      await serviceRegistration.confirmContract({});
      setShow(true);
      setDefaultContractText('Assinar e continuar');
      setTimeout(() => {
        navigate('/step/bank');
      }, 3000);
    } catch (error) {
      setError(true);
      setDefaultContractText('Assinar e continuar');
    }
  };

  const handleConfirmContractHunting = async () => {
    setShow(true);
    gtmEventsTracker(gtmAttributesSignedContract);
    try {
      await serviceRegistration.confirmContractHunting({});
      navigate('/step/bank');
    } catch (error) {
      setError(true);
    }
  };

  const current: number = 2;
  const steps: Array<StepInterface> = Step.steps();

  const contractCustom = (
    <>
      <RenderDocuments pdf={contract_url} />
      <div className="flex justify-center">
        <Button
          text="Validar e continuar"
          variant="primary"
          type="button"
          onClick={handleConfirmContractHunting}
          id="mp__validate-contract-custom-button"
        />
      </div>
    </>
  );

  const contractDefault = (
    <>
      <DefaultContract />
      <div className="flex justify-center mb-[27px]">
        <Checkbox
          label="Li todo o contrato e aceito as condições"
          value={acceptContract}
          onChange={handleAcceptContract}
          disabled={disableButton}
        />
      </div>
      <div className="flex justify-center">
        <Button
          text={defaultContractText}
          variant="primary"
          type="button"
          id="mp__validate-contract-default-button"
          onClick={handleClick}
          disabled={disableButton || !acceptContract}
        />
      </div>
    </>
  );

  const contractHunting: any = {
    default: contractDefault,
    custom: contractCustom,
  };

  const sellerContract =
    seller_entrance == 'hunting'
      ? contractHunting[seller?.seller.contract.type ?? 'default']
      : contractDefault;

  return (
    <>
      <div className="px-5 py-6 md:px-0 md:py-0">
        <div className="mb-12 max-[768px]:hidden">
          <ProgressStep steps={steps} current={current} />
        </div>
        <div className="hidden max-[768px]:block mb-12">
          <ProgressBar
            total={steps.length}
            step={current}
            subtitle={steps.find((e) => e.step === current)?.title ?? ''}
          />
        </div>
        <div className="text-center mb-10 max-[768px]:hidden">
          <Typography as="p" variant="subtitle2" className="text-neutral-darkest">
            Leia todo o contrato com atenção.
          </Typography>
          <Typography as="p" variant="body2" className="text-neutral-darkest">
            Se ficar com alguma dúvida, entre em contato com nosso suporte.
          </Typography>
        </div>
        <div className="hidden max-[768px]:block pb-6 w-[95%]">
          <Typography as="p" variant="subtitle2" className="text-neutral-darkest">
            Leia todo o contrato com atenção.
          </Typography>
          <Typography as="p" variant="body2" className="text-neutral-darkest">
            Se ficar com alguma dúvida, entre em contato com nosso suporte.
          </Typography>
        </div>
        {seller && (
          <section className="grid justify-center items-center max-w-[44.4rem] mx-auto mb-[11.75rem] max-[768px]:max-w-full max-[768px]:mb-0">
            {contractDefault}
          </section>
        )}
      </div>
      <div className="absolute top-[3.688rem] right-[5.5rem] w-[23.625rem] max-[768px]:top-[4.688rem] max-[768px]:w-full max-[768px]:right-0 max-[768px]:px-5 max-[768px]:py-6">
        <Alert
          show={show && !error}
          variant="success"
          onClose={() => setShow(false)}
          dismissible
          closeVariant="white"
        >
          {sellerContract == 'default' ? (
            <Typography>
              <strong>Contrato assinado!</strong> Enviamos uma cópia do contrato para seu email.
            </Typography>
          ) : (
            <Typography>
              <strong>Contrato validado!</strong> Enviamos uma cópia do contrato para seu email.
            </Typography>
          )}
        </Alert>
        <Alert
          show={show && error}
          variant="error"
          onClose={() => {
            setError(false);
            setShow(false);
          }}
          dismissible
          closeVariant="white"
        >
          <Typography as="p">Erro ao assinar o contrato</Typography>
        </Alert>
        <Alert
          show={warning}
          variant="error"
          onClose={() => {
            setWarning(false);
          }}
          dismissible
          closeVariant="white"
        >
          <Typography as="p">Token inválido, faça o login para acessar seu contrato!</Typography>
        </Alert>
      </div>
    </>
  );
};

export default Contract;
